import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export function CTASection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".cta-title",
        { opacity: 0, y: 40, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            once: true,
          },
        }
      );

      gsap.fromTo(
        ".cta-description",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          delay: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            once: true,
          },
        }
      );

      gsap.fromTo(
        ".cta-button",
        { opacity: 0, y: 20, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          delay: 0.4,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            once: true,
          },
        }
      );

      gsap.fromTo(
        ".cta-quote",
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          delay: 0.6,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            once: true,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-48 bg-secondary/30 relative overflow-hidden border-t border-border">
      <div className="absolute inset-0 bg-secondary/10" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent opacity-40" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="cta-title text-6xl sm:text-8xl lg:text-9xl font-medium text-foreground mb-12 max-w-4xl mx-auto leading-[0.85] tracking-tighter opacity-0">
          Access Precision <br/> <span className="text-accent italic font-light">Capital.</span>
        </h2>
        <p className="cta-description text-base text-muted-foreground mb-16 max-w-xl mx-auto font-light leading-relaxed tracking-wide opacity-0">
          Partner with Godman Capital for speed, structure, and sophisticated financing solutions 
          that redefine business growth in India.
        </p>
        
        <div className="flex flex-wrap justify-center gap-0">
           <Link href="/contact">
             <Button
               size="lg"
               className="cta-button bg-accent text-white hover:bg-accent/90 border border-accent rounded-none px-12 py-8 h-auto text-sm tracking-[0.2em] transition-all duration-500 uppercase opacity-0 group"
               data-testid="button-cta-book-call"
             >
               Engage With Us
             </Button>
           </Link>
           <Link href="/services">
             <Button
               size="lg"
               variant="outline"
               className="cta-button border-border text-foreground bg-secondary/50 border-l-0 hover:bg-secondary rounded-none px-12 py-8 h-auto text-sm tracking-[0.2em] transition-all duration-500 uppercase opacity-0 group"
               data-testid="button-cta-explore"
             >
               Capabilities
             </Button>
           </Link>
        </div>
        
        <div className="cta-quote mt-24 pt-12 border-t border-border opacity-0">
          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.4em]">
            "We fund what others fear."
          </p>
        </div>
      </div>
    </section>
  );
}
